/**
 * KOMONO Store Locator
 * Enhanced implementation with geolocation and auto-updating results
 */

class StoreLocator {
  constructor(config) {
    // Configuration
    this.config = config || window.storeLocatorConfig || {};
    this.STOREFRONT_ACCESS_TOKEN = this.config.storefrontToken || '76249782dd572514a4ca845b94d77d7d';
    this.SHOP_DOMAIN = this.config.shopDomain || window.Shopify?.shop || window.location.hostname;
    this.KOMONO_BADGE_URL = this.config.komonoStoreBadgeUrl || '';

    // State
    this.stores = [];
    this.filteredStores = [];
    this.markers = [];
    this.map = null;
    this.infoWindow = null;
    this.countries = new Set();
    this.cities = {};
    this.userLocation = null;
    this.currentPage = 1;
    this.storesPerPage = 5;
    this.isLoading = false;
    this.showTooltips = true;
    this.showInfoWindows = true;

    // DOM Elements
    this.elements = {
      useLocationBtn: document.getElementById('useMyLocation'),
      locationStatus: document.getElementById('locationStatus'),
      countryFilter: document.getElementById('countryFilter'),
      cityFilter: document.getElementById('cityFilter'),
      storeList: document.getElementById('storeList'),
      storeCount: document.getElementById('storeCount'),
      locationDisplay: document.getElementById('locationDisplay'),
      storeLocatorResults: document.getElementById('storeLocatorResults'),
      storeLocatorLoading: document.getElementById('storeLocatorLoading'),
      productFilters: document.querySelectorAll('input[name="product-filter"]')
    };

    // Initialize
    this.init();
  }

  async init() {
    try {
      this.showLoading('Loading store data...');

      // Fetch all store locations using pagination
      await this.fetchAllStoreLocations();

      // Set up event listeners
      this.setupEventListeners();

      // Initialize Google Maps
      if (window.google && window.google.maps) {
        this.initMap();
      } else {
        window.initMap = () => this.initMap();
      }

      // Try to get user's location
      this.promptForLocation();

      this.hideLoading();
      // console.log(`Loaded ${this.stores.length} stores successfully`);
    } catch (error) {
      console.error('Error initializing store locator:', error);
      this.showError('Failed to initialize store locator. Please try again later.');
      this.hideLoading();
    }
  }

  async fetchAllStoreLocations() {
    try {
      let hasNextPage = true;
      let cursor = null;
      let allStores = [];

      // Fetch all pages of results
      while (hasNextPage) {
        const { stores, endCursor, hasNext } = await this.fetchStoreLocationsPage(cursor);
        allStores = [...allStores, ...stores];
        cursor = endCursor;
        hasNextPage = hasNext;
      }

      // Process all stores
      this.stores = allStores;

      // Extract countries and cities
      this.extractLocationData();

      // Populate country dropdown
      this.populateCountryDropdown();

    } catch (error) {
      console.error('Error fetching all store locations:', error);
      throw error;
    }
  }

  async fetchStoreLocationsPage(cursor = null) {
    try {
      // GraphQL query to fetch store_locations metaobjects with pagination
      const graphqlQuery = {
        query: `
          query GetStoreLocations($first: Int!, $after: String) {
            metaobjects(type: "store_locations", first: $first, after: $after) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        `,
        variables: {
          first: 250, // Maximum per page
          after: cursor
        }
      };

      const response = await fetch(`https://${this.SHOP_DOMAIN}/api/2025-07/graphql.json`, {
        method: 'POST',
        headers: {
          "X-Shopify-Storefront-Access-Token": this.STOREFRONT_ACCESS_TOKEN,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(graphqlQuery)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error(data.errors[0].message);
      }

      if (!data.data || !data.data.metaobjects) {
        throw new Error('Invalid API response format');
      }

      // Extract page info
      const pageInfo = data.data.metaobjects.pageInfo;

      // Process the metaobjects
      const stores = data.data.metaobjects.edges.map(({ node }) => {
        // Convert fields array to an object
        const storeData = {};

        node.fields.forEach(field => {
          storeData[field.key] = field.value;
        });

        // Parse products JSON string to array
        try {
          storeData.products = JSON.parse(storeData.products || '[]');
        } catch (e) {
          storeData.products = [];
        }

        return {
          id: node.id,
          handle: node.handle,
          ...storeData
        };
      });

      return {
        stores,
        endCursor: pageInfo.endCursor,
        hasNext: pageInfo.hasNextPage
      };

    } catch (error) {
      console.error('Error fetching store locations page:', error);
      throw error;
    }
  }

  extractLocationData() {
    // Extract countries and cities from store data
    this.stores.forEach(store => {
      if (store.country) {
        this.countries.add(store.country);

        if (!this.cities[store.country]) {
          this.cities[store.country] = new Set();
        }

        if (store.city) {
          this.cities[store.country].add(store.city);
        }
      }
    });
  }

  populateCountryDropdown() {
    const sortedCountries = Array.from(this.countries).sort();

    this.elements.countryFilter.innerHTML = '<option value="">Select Country</option>';

    sortedCountries.forEach(country => {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      this.elements.countryFilter.appendChild(option);
    });
  }

  populateCityDropdown(country) {
    this.elements.cityFilter.innerHTML = '<option value="">All Cities</option>';

    if (!country || !this.cities[country]) {
      this.elements.cityFilter.disabled = true;
      return;
    }

    const sortedCities = Array.from(this.cities[country]).sort();

    sortedCities.forEach(city => {
      const option = document.createElement('option');
      option.value = city;
      option.textContent = city;
      this.elements.cityFilter.appendChild(option);
    });

    this.elements.cityFilter.disabled = false;
  }

  setupEventListeners() {
    // Use my location button
    if (this.elements.useLocationBtn) {
      this.elements.useLocationBtn.addEventListener('click', () => {
        this.promptForLocation(true);
      });
    }

    // Country filter change
    this.elements.countryFilter.addEventListener('change', () => {
      const country = this.elements.countryFilter.value;
      this.populateCityDropdown(country);

      if (country) {
        // Special case for Belgium - auto-select Antwerp if available
        if (country === 'Belgium') {
          const antwerpOption = Array.from(this.elements.cityFilter.options)
            .find(option => option.text === 'Antwerp');

          if (antwerpOption) {
            this.elements.cityFilter.value = 'Antwerp';
          } else if (this.elements.cityFilter.options.length > 1) {
            // If Antwerp isn't available, fall back to first city
            this.elements.cityFilter.selectedIndex = 1;
          }
        }
        // For all other countries, select the first city
        else if (this.elements.cityFilter.options.length > 1) {
          this.elements.cityFilter.selectedIndex = 1;
        }

        // Immediately filter stores
        this.filterStores();
      } else {
        // Hide results if no country selected
        this.elements.storeLocatorResults.style.display = 'none';
      }
    });

    // City filter change
    this.elements.cityFilter.addEventListener('change', () => {
      // Immediately filter stores when city changes
      if (this.elements.countryFilter.value) {
        this.filterStores();
      }
    });

    // Product filter changes
    this.elements.productFilters.forEach(filter => {
      filter.addEventListener('change', () => {
        // Update styling for this filter
        this.updateFilterButtonStyle(filter);

        // Only filter if a country is selected
        if (this.elements.countryFilter.value) {
          this.filterStores();
        }
      });
    });

    // Initialize filter button styles
    this.elements.productFilters.forEach(filter => {
      this.updateFilterButtonStyle(filter);
    });
  }

  updateFilterButtonStyle(filterCheckbox) {
    // Find the parent label element
    const filterButton = filterCheckbox.closest('.filter-option');

    if (filterButton) {
      // Add or remove 'checked' class based on checkbox state
      if (filterCheckbox.checked) {
        filterButton.classList.add('checked');
      } else {
        filterButton.classList.remove('checked');
      }
    }
  }

  promptForLocation(userTriggered = false) {
    if (!navigator.geolocation) {
      if (userTriggered) {
        this.elements.locationStatus.textContent = 'Geolocation is not supported by your browser';
        setTimeout(() => {
          this.elements.locationStatus.textContent = '';
        }, 3000);
      }
      return;
    }

    if (userTriggered) {
      this.showLoading('Finding your location...');
    }

    navigator.geolocation.getCurrentPosition(
      position => this.handleLocationSuccess(position),
      error => this.handleLocationError(error, userTriggered),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  async handleLocationSuccess(position) {
    try {
      this.userLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      // console.log('User location:', this.userLocation);

      // Find closest stores and determine user's country/city
      const closestStores = this.findClosestStores(this.userLocation, 20);

      // Try to get country and city from reverse geocoding
      const locationInfo = await this.reverseGeocode(this.userLocation);

      if (locationInfo.country) {
        // Set country in dropdown
        if (this.countries.has(locationInfo.country)) {
          this.elements.countryFilter.value = locationInfo.country;
          this.populateCityDropdown(locationInfo.country);

          // Set city if available
          if (locationInfo.city && this.cities[locationInfo.country]?.has(locationInfo.city)) {
            this.elements.cityFilter.value = locationInfo.city;
          }

          // Filter stores automatically
          this.filterStores();
        } else {
          // Country not in our list, just show nearest stores
          this.filteredStores = closestStores;
          this.elements.storeCount.textContent = this.filteredStores.length;
          this.elements.locationDisplay.textContent = `YOUR LOCATION`;
          this.elements.storeLocatorResults.style.display = 'flex';
          this.displayStores();
          this.updateMap();
        }
      } else {
        // Just show nearest stores
        this.filteredStores = closestStores;
        this.elements.storeCount.textContent = this.filteredStores.length;
        this.elements.locationDisplay.textContent = `YOUR LOCATION`;
        this.elements.storeLocatorResults.style.display = 'flex';
        this.displayStores();
        this.updateMap();
      }

      this.hideLoading();

    } catch (error) {
      console.error('Error handling location success:', error);
      this.hideLoading();
    }
  }

  handleLocationError(error, userTriggered) {
    let message = '';

    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = 'Location permission denied';
        break;
      case error.POSITION_UNAVAILABLE:
        message = 'Location information unavailable';
        break;
      case error.TIMEOUT:
        message = 'Location request timed out';
        break;
      default:
        message = 'An unknown error occurred';
        break;
    }

    console.warn('Geolocation error:', message);

    if (userTriggered) {
      this.elements.locationStatus.textContent = message;
      this.elements.locationStatus.classList.add('error');
      setTimeout(() => {
        this.elements.locationStatus.classList.remove('error');
      }, 2500);
      setTimeout(() => {
        this.elements.locationStatus.textContent = '';
      }, 3000);
    }

    this.hideLoading();
  }

  async reverseGeocode(location) {
    try {
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${this.getGoogleMapsApiKey()}`);
      const data = await response.json();

      if (data.status !== 'OK') {
        throw new Error(`Geocoding error: ${data.status}`);
      }

      // Extract country and city from address components
      let country = '';
      let city = '';

      if (data.results && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;

        for (const component of addressComponents) {
          if (component.types.includes('country')) {
            country = component.long_name;
          }

          if (component.types.includes('locality') || component.types.includes('administrative_area_level_1')) {
            city = component.long_name;
          }
        }
      }

      return { country, city };
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return { country: '', city: '' };
    }
  }

  getGoogleMapsApiKey() {
    const scriptSrc = document.querySelector('script[src*="maps.googleapis.com/maps/api/js?key="]')?.src;
    if (scriptSrc) {
      const match = scriptSrc.match(/key=([^&]*)/);
      return match ? match[1] : '';
    }
    return '';
  }

  findClosestStores(location, limit = 20) {
    // Calculate distance for each store
    const storesWithDistance = this.stores.map(store => {
      const lat = parseFloat(store.latitude);
      const lng = parseFloat(store.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        return { ...store, distance: Infinity };
      }

      const distance = this.calculateDistance(
        location.lat, location.lng,
        lat, lng
      );

      return { ...store, distance };
    });

    // Sort by distance and take the first 'limit' stores
    return storesWithDistance
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    // Haversine formula to calculate distance between two points
    const R = 6371; // Radius of the earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  filterStores() {
    const country = this.elements.countryFilter.value;
    const city = this.elements.cityFilter.value;
    const selectedProducts = Array.from(this.elements.productFilters)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    if (!country) {
      return;
    }

    this.showLoading('Filtering stores...');

    this.filteredStores = this.stores.filter(store => {
      // Filter by country
      if (country && store.country !== country) {
        return false;
      }

      // Filter by city if selected
      if (city && store.city !== city) {
        return false;
      }

      // Product filtering: only apply if at least one product is selected
      if (selectedProducts.length > 0) {
        // Check if store has ANY of the selected products
        const hasSelectedProduct = selectedProducts.every(product =>
          store.products.includes(product)
        );

        if (!hasSelectedProduct) {
          return false;
        }
      }

      return true;
    });

    // If we have user location, sort by distance
    if (this.userLocation) {
      this.filteredStores = this.filteredStores.map(store => {
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          return { ...store, distance: Infinity };
        }

        const distance = this.calculateDistance(
          this.userLocation.lat, this.userLocation.lng,
          lat, lng
        );

        return { ...store, distance };
      }).sort((a, b) => a.distance - b.distance);
    }

    // Update display
    this.elements.storeCount.textContent = this.filteredStores.length;

    // Set location display
    if (city) {
      this.elements.locationDisplay.textContent = `${city.toUpperCase()}, ${country.toUpperCase()}`;
    } else {
      this.elements.locationDisplay.textContent = country.toUpperCase();
    }

    // Display results
    this.elements.storeLocatorResults.style.display = 'flex';

    // Update list and map
    this.displayStores();
    this.updateMap();

    this.hideLoading();
  }

  displayStores() {
    this.elements.storeList.innerHTML = '';

    if (this.filteredStores.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'no-results';
      noResults.textContent = 'No stores found matching your criteria.';
      this.elements.storeList.appendChild(noResults);
      return;
    }

    // Display top 20 results without pagination
    const storesToShow = this.filteredStores.slice(0, 20);

    // Create store list items
    storesToShow.forEach(store => {
      const storeEl = document.createElement('div');
      storeEl.className = 'store-item';

      // Determine optical collection type
      let opticalCollectionText = '';
      if (store.kind === 'optical-a') {
        opticalCollectionText = '<div class="store-collection-type">Full Optical Collection</div>';
      } else if (store.kind === 'optical-b' || store.kind === 'optical-c') {
        opticalCollectionText = '<div class="store-collection-type">Limited Optical Collection</div>';
      }

      // KOMONO Store Badge
      let storeBadge = '';
      if (store.is_official_komono_store === 'true') {
        storeBadge = `
          <div class="product__badge">
            <span class="badge__icon">
              ${this.KOMONO_BADGE_URL ? `<img src="${this.KOMONO_BADGE_URL}"/>` : ''}
            </span>
            <span class="badge badge--odyssey">KOMONO STORE</span>
          </div>
        `;
      }

      // Format distance if available
      let distanceText = '';
      if (store.distance !== undefined && store.distance !== Infinity) {
        distanceText = `<div class="store-distance">${store.distance.toFixed(1)} km</div>`;
      }

      storeEl.innerHTML = `
        <div class="store-item-header">
          <h3 class="store-name h4">${store.title}</h3>
          ${storeBadge}
        </div>
        <div class="store-address">
          ${store.address || `${store.street || ''} ${store.postal_code || ''} ${store.city || ''}`}
        </div>
        ${distanceText}
        ${opticalCollectionText}
        <div class="store-contact-info">
        ${store.phone ? `<div class="store-phone link link-underline-start">${store.phone}</div>` : ''}
        ${store.email ? `<div class="store-email link link-underline-start"><a href="mailto:${store.email}">${store.email}</a></div>` : ''}
        ${store.website ? `<div class="store-website link link-underline-start"><a href="${store.website}" target="_blank">${store.website.replace(/^https?:\/\//, '')}</a></div>` : ''}
        </div>
        <button class="get-directions-btn link link-underline-start" data-lat="${store.latitude}" data-lng="${store.longitude}">Get Directions</button>
      `;

      this.elements.storeList.appendChild(storeEl);

      // Add event listener for Get Directions button
      const directionsBtn = storeEl.querySelector('.get-directions-btn');
      directionsBtn.addEventListener('click', () => {
        const lat = directionsBtn.getAttribute('data-lat');
        const lng = directionsBtn.getAttribute('data-lng');
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
      });
    });
  }

  addPagination() {
    const totalPages = Math.ceil(this.filteredStores.length / this.storesPerPage);

    const paginationEl = document.createElement('div');
    paginationEl.className = 'pagination';

    // Previous button
    const prevBtn = document.createElement('button');
    prevBtn.className = 'page-btn prev';
    prevBtn.innerHTML = '&lt;';
    prevBtn.disabled = this.currentPage === 1;
    prevBtn.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.displayStores();
      }
    });

    // Page info
    const pageInfo = document.createElement('span');
    pageInfo.className = 'page-info';
    pageInfo.textContent = `${this.currentPage} / ${totalPages}`;

    // Next button
    const nextBtn = document.createElement('button');
    nextBtn.className = 'page-btn next';
    nextBtn.innerHTML = '&gt;';
    nextBtn.disabled = this.currentPage === totalPages;
    nextBtn.addEventListener('click', () => {
      if (this.currentPage < totalPages) {
        this.currentPage++;
        this.displayStores();
      }
    });

    paginationEl.appendChild(prevBtn);
    paginationEl.appendChild(pageInfo);
    paginationEl.appendChild(nextBtn);

    this.elements.storeList.appendChild(paginationEl);
  }

  initMap() {
    // Create map with a style similar to the reference image
    this.map = new google.maps.Map(document.getElementById('storeMap'), {
      center: { lat: 50.8503, lng: 4.3517 }, // Brussels
      zoom: 13,
      styles: [
        {
          "featureType": "all",
          "elementType": "labels.text.fill",
          "stylers": [{ "saturation": 36 }, { "color": "#333333" }, { "lightness": 40 }]
        },
        {
          "featureType": "all",
          "elementType": "labels.text.stroke",
          "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "lightness": 16 }]
        },
        {
          "featureType": "all",
          "elementType": "labels.icon",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "administrative",
          "elementType": "geometry.fill",
          "stylers": [{ "color": "#fefefe" }, { "lightness": 20 }]
        },
        {
          "featureType": "administrative",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#fefefe" }, { "lightness": 17 }, { "weight": 1.2 }]
        },
        {
          "featureType": "landscape",
          "elementType": "geometry",
          "stylers": [{ "color": "#f5f5f5" }, { "lightness": 20 }]
        },
        {
          "featureType": "poi",
          "elementType": "geometry",
          "stylers": [{ "color": "#f5f5f5" }, { "lightness": 21 }]
        },
        {
          "featureType": "poi",
          "elementType": "labels",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry.fill",
          "stylers": [{ "color": "#ffffff" }, { "lightness": 17 }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#ffffff" }, { "lightness": 29 }, { "weight": 0.2 }]
        },
        {
          "featureType": "road.arterial",
          "elementType": "geometry",
          "stylers": [{ "color": "#ffffff" }, { "lightness": 18 }]
        },
        {
          "featureType": "road.local",
          "elementType": "geometry",
          "stylers": [{ "color": "#ffffff" }, { "lightness": 16 }]
        },
        {
          "featureType": "transit",
          "elementType": "geometry",
          "stylers": [{ "color": "#f2f2f2" }, { "lightness": 19 }]
        },
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{ "color": "#e9e9e9" }, { "lightness": 17 }]
        }
      ],
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false
    });

    this.infoWindow = new google.maps.InfoWindow();

    // Define marker icons
    this.regularMarkerIcon = {
      url: window.storeLocatorConfig?.regularMarkerIcon || 'https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png',
      fillColor: "#000000",
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 1,
      scaledSize: new google.maps.Size(24, 32),
      anchor: new google.maps.Point(12, 30),
      labelOrigin: new google.maps.Point(12, 12)
    };

    // Add the watch marker icon
    this.watchMarkerIcon = {
      url: window.storeLocatorConfig?.regularWatchIcon || 'https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png',
      fillColor: "#000000",
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 1,
      scaledSize: new google.maps.Size(24, 32),
      anchor: new google.maps.Point(12, 30),
      labelOrigin: new google.maps.Point(12, 12)
    };

    // console.log('KOMONO Marker Icon URL:', window.storeLocatorConfig?.komonoMarkerIcon);

    this.komonoMarkerIcon = {
      url: window.storeLocatorConfig?.komonoMarkerIcon || 'https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi-dotless2.png',
      fillColor: "#000000",
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 1.2,
      scaledSize: new google.maps.Size(24, 32),
      anchor: new google.maps.Point(12, 30),
      labelOrigin: new google.maps.Point(12, 12)
    };

    // User location icon
    this.userLocationIcon = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: "#4285F4",
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 2
    };
  }

  updateMap() {
    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    const bounds = new google.maps.LatLngBounds();
    let userMarker = null;
    let firstMarker = null;

    // If we have user location, include it in bounds and add marker
    if (this.userLocation) {
      bounds.extend(this.userLocation);

      // Add user location marker
      userMarker = new google.maps.Marker({
        position: this.userLocation,
        map: this.map,
        icon: this.userLocationIcon,
        title: "Your Location",
        zIndex: 1
      });
    }

    // Add markers for ALL filtered stores that match the criteria, not just the first 20
    const country = this.elements.countryFilter.value;
    const city = this.elements.cityFilter.value;

    if (!country) return;

    const selectedProducts = Array.from(this.elements.productFilters)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    // Check if only watches are selected
    const onlyWatchesSelected = selectedProducts.length === 1 && selectedProducts[0] === 'watches';

    // Get all stores that match the filters
    const allMatchingStores = this.stores.filter(store => {
      // Filter by country
      if (country && store.country !== country) {
        return false;
      }

      // Filter by city if selected
      if (city && store.city !== city) {
        return false;
      }

      // Product filtering: only apply if at least one product is selected
      if (selectedProducts.length > 0) {
        // Check if store has ALL of the selected products
        const hasAllSelectedProducts = selectedProducts.every(product =>
          store.products.includes(product)
        );

        if (!hasAllSelectedProducts) {
          return false;
        }
      }

      return true;
    });

    // Add markers for all matching stores
    allMatchingStores.forEach((store, index) => {
      const lat = parseFloat(store.latitude);
      const lng = parseFloat(store.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        return;
      }

      const position = { lat, lng };

      // Determine which icon to use
      const isOfficialStore = store.is_official_komono_store === 'true';

      // Check if store only has watches
      const storeProducts = store.products || [];
      const onlyHasWatches = storeProducts.includes('watches') &&
        !storeProducts.includes('sunglasses') &&
        !storeProducts.includes('opticals') &&
        !storeProducts.includes('junior');

      // Determine the correct icon to use
      let icon;
      if (isOfficialStore) {
        icon = this.komonoMarkerIcon; // KOMONO stores always use KOMONO icon
      } else if (onlyWatchesSelected || onlyHasWatches) {
        icon = this.watchMarkerIcon; // Use watch icon if only watches selected or store only has watches
      } else {
        icon = this.regularMarkerIcon; // Default icon for other cases
      }

      // Check if this store is in the top 20 displayed in the list
      const isInTopResults = this.filteredStores.slice(0, 20).some(s => s.id === store.id);
      const storeIndex = this.filteredStores.findIndex(s => s.id === store.id);

      // Create marker
      const marker = new google.maps.Marker({
        position,
        map: this.map,
        icon: icon,
        title: store.title, // Basic tooltip
        zIndex: isOfficialStore ? 3 : 2,
        storeId: store.id,
        storeIndex: storeIndex
      });

      // Store the first marker from the list (top 20)
      if (index === 0 && !firstMarker) {
        firstMarker = marker;
      }

      bounds.extend(position);

      // Enhanced tooltip on hover using InfoWindow
      const infoContent = `
        <div class="map-tooltip h5">
          <strong>${store.title}</strong>
          ${isOfficialStore ? '<div class="tooltip-badge">KOMONO STORE</div>' : ''}
        </div>
      `;

      if (this.showTooltips) {
        marker.addListener('mouseover', () => {
          this.infoWindow.setContent(infoContent);
          this.infoWindow.open(this.map, marker);
        });

        marker.addListener('mouseout', () => {
          // this.infoWindow.close();
        });
      }

      // Add click event
      marker.addListener('click', () => {
        // If this store is in the top 20 displayed in the list, highlight it
        if (isInTopResults && storeIndex >= 0) {
          this.highlightStoreInList(storeIndex);
        }

        if (this.showInfoWindows) {
          // Show store info in a more detailed InfoWindow
          const detailedInfoContent = `
            <div class="map-info-window">
              <div class="map-info-header">
                <h3 class="h4">${store.title}</h3>
                ${isOfficialStore ? '<div class="tooltip-badge">KOMONO STORE</div>' : ''}
              </div>
              <p>${store.address || `${store.street || ''} ${store.postal_code || ''} ${store.city || ''}`}
              ${store.country ? `<br>${store.country}` : ''}</p>
              ${store.phone ? `<a href="tel:${store.phone}" class="link link-underline-start">${store.phone}</a>` : ''}
              <button class="link link-underline-start" onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}', '_blank')">Get Directions</button>
            </div>
          `;

          this.infoWindow.setContent(detailedInfoContent);
          this.infoWindow.open(this.map, marker);
        }

        // Center map on this marker
        this.map.panTo(position);
        this.map.setZoom(15); // Zoom in when clicked
      });

      this.markers.push(marker);
    });

    // Fit map to bounds
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);

      // Now focus on the first marker or user location
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        // Focus priority: 1. First store in list, 2. User location
        if (firstMarker) {
          // Focus on first store in the list
          this.map.setCenter(firstMarker.getPosition());
          this.map.setZoom(13); // Good zoom level for a city area
        } else if (userMarker) {
          // If no stores but we have user location
          this.map.setCenter(userMarker.getPosition());
          this.map.setZoom(12);
        }

        // If there are fewer than 3 markers, zoom in more
        if (this.markers.length < 3 && this.markers.length > 0) {
          this.map.setZoom(14);
        }
      });
    }

    // Add CSS for tooltips
    if (!document.getElementById('map-tooltip-styles')) {
      const style = document.createElement('style');
      style.id = 'map-tooltip-styles';
      style.textContent = `
        .map-tooltip {
          padding: 5px 10px;
          font-family: sans-serif;
        }
        .tooltip-badge {
          display: inline-block;
          border: solid 1px #000;
          color: #000;
          font-size: 10px;
          padding: 2px 5px;
          margin-top: 3px;
        }
        .map-info-window {
          padding: 10px;
          max-width: 250px;
        }
        .map-info-window h3 {
          margin-top: 0;
          margin-bottom: 5px;
        }
        .info-badge {
          display: inline-block;
          background: #000;
          color: #fff;
          font-size: 11px;
          padding: 3px 6px;
          margin-bottom: 8px;
        }
        .info-directions-btn {
          background: #000;
          color: #fff;
          border: none;
          padding: 8px 12px;
          margin-top: 8px;
          cursor: pointer;
          font-weight: bold;
        }
      `;
      document.head.appendChild(style);
    }
  }

  highlightStoreInList(index) {
    // Find the store element
    const storeElements = this.elements.storeList.querySelectorAll('.store-item');
    if (index >= storeElements.length) return;

    const storeElement = storeElements[index];

    // Add highlight class
    storeElement.classList.add('clicked');

    // Remove class after animation completes
    setTimeout(() => {
      storeElement.classList.remove('clicked');
    }, 100);

    // Scroll to the element
    // storeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.scrollWithOffset(storeElement, 0.3);
  }

  scrollWithOffset(element, offsetPercentage = 0.2) {
    if (!element) return;

    // Get element position
    const rect = element.getBoundingClientRect();
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate offset (percentage of viewport height)
    const offsetPixels = window.innerHeight * offsetPercentage;

    // Calculate final scroll position with offset
    const scrollToY = currentScrollTop + rect.top - offsetPixels;

    // Smoothly scroll to the position
    window.scrollTo({
      top: scrollToY,
      behavior: 'smooth'
    });
  }

  showLoading(message = 'Loading...') {
    this.isLoading = true;
    if (this.elements.storeLocatorLoading) {
      const loadingText = this.elements.storeLocatorLoading.querySelector('.loading-text');
      if (loadingText) {
        loadingText.textContent = message;
      }
      this.elements.storeLocatorLoading.style.display = 'flex';
    }
  }

  hideLoading() {
    this.isLoading = false;
    if (this.elements.storeLocatorLoading) {
      this.elements.storeLocatorLoading.style.display = 'none';
    }
  }

  showError(message) {
    const errorEl = document.createElement('div');
    errorEl.className = 'store-locator-error';
    errorEl.textContent = message;

    this.elements.storeList.innerHTML = '';
    this.elements.storeList.appendChild(errorEl);
  }
}

// Initialize store locator when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new StoreLocator(window.storeLocatorConfig);
});

{/* <div style="position: absolute; left: 0px; top: 0px; z-index: 107; width: 100%;">
  <div style="cursor: default; position: absolute; top: 0px; left: 0px; z-index: -130;">
    <div class="gm-style-iw-a" style="position: absolute; left: 0px; top: 0px;">
      <div class="gm-style-iw-t" style="right: 0px; bottom: 41px;">
        <div role="dialog" tabindex="-1" class="gm-style-iw gm-style-iw-c" aria-labelledby="5BB18669-8E9D-4DC2-8450-C611CE9A20B9" style="padding-inline-end: 0px; padding-bottom: 0px; padding-top: 0px; max-width: 648px; max-height: 739px; min-width: 0px;">
          <div class="gm-style-iw-chr">
            <div class="gm-style-iw-ch" id="5BB18669-8E9D-4DC2-8450-C611CE9A20B9"></div>
            <button draggable="false" aria-label="Close" title="Close" type="button" class="gm-ui-hover-effect" style="background: none; display: block; border: 0px; margin: 0px; padding: 0px; text-transform: none; appearance: none; position: relative; cursor: pointer; user-select: none; width: 48px; height: 48px;" tabindex="0">
              <span style="mask-image: url(&quot;data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20d%3D%22M19%206.41L17.59%205%2012%2010.59%206.41%205%205%206.41%2010.59%2012%205%2017.59%206.41%2019%2012%2013.41%2017.59%2019%2019%2017.59%2013.41%2012z%22/%3E%3Cpath%20d%3D%22M0%200h24v24H0z%22%20fill%3D%22none%22/%3E%3C/svg%3E&quot;); pointer-events: none; display: block; width: 24px; height: 24px; margin: 12px;"></span>
            </button>
          </div>
            <div class="gm-style-iw-d" style="overflow: scroll; max-height: 721px;"><div>
            <div class="map-tooltip">
              <strong>KOMONO Brussels</strong>
              <div class="tooltip-badge">KOMONO STORE</div>
            </div>
          </div>
          </div>
        </div><div class="gm-style-iw-tc"></div>
      </div>
    </div>
  </div>
</div> */}